/**
 * EntryHistory — List of recent time entries for the selected contract.
 * Extracted from TimeTracking.jsx for maintainability.
 */
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getAbsenceLabel } from '../../utils/constants';

export const EntryHistory = ({ entries, selectedContract, contracts, monthRef }) => {
  const filteredEntries = entries.filter(e => !selectedContract || e.contract_id === selectedContract);
  const displayEntries = filteredEntries.slice(0, 20);
  const totalForContract = selectedContract
    ? entries.filter(e => e.contract_id === selectedContract).length
    : entries.length;

  // Derive month label from monthRef or from entries
  const monthLabel = (() => {
    if (!monthRef) return '';
    if (monthRef instanceof Date) {
      return monthRef.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
    }
    // If it's a string like "2026-04", parse it
    if (typeof monthRef === 'string') {
      const d = new Date(monthRef + '-01');
      return d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }).replace(/^\w/, c => c.toUpperCase());
    }
    return '';
  })();

  // Helper: get child name by contract_id
  const getChildName = (contractId) => {
    const c = contracts?.find(ct => ct.id === contractId);
    return c?.child_first_name || c?.child_name || '';
  };

  const showChildName = !selectedContract && contracts && contracts.length > 1;

  return (
    <Card data-testid="entries-history">
      <CardHeader>
        <CardTitle className="text-lg" style={{ fontFamily: 'var(--font-secondary)' }}>
          Historique {monthLabel ? `— ${monthLabel}` : 'du mois'}
        </CardTitle>
        <CardDescription>
          {totalForContract} entrée(s) ce mois
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 max-h-72 overflow-y-auto">
          {displayEntries.map(e => (
            <div
              key={e.id}
              className={`flex items-center justify-between py-2 px-3 rounded-lg border text-sm ${
                e.is_absent
                  ? 'bg-[var(--warning)]/10 border-[var(--warning)]/30'
                  : 'border-[var(--neutral-200)]'
              }`}
              data-testid={`history-entry-${e.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {showChildName && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 shrink-0" style={{ borderColor: 'var(--primary-300)', color: 'var(--primary-500)' }}>
                    {getChildName(e.contract_id)}
                  </Badge>
                )}
                <span className="font-medium shrink-0">
                  {new Date(e.date + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}
                </span>
                {e.is_absent ? (
                  <Badge variant="outline" className="bg-[var(--warning)]/10 text-[var(--warning)] border-[var(--warning)] text-xs">
                    {getAbsenceLabel(e.absence_type)}
                  </Badge>
                ) : (
                  <span className="text-[var(--text-secondary)] truncate">
                    {e.start_time ? new Date(e.start_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                    {' → '}
                    {e.end_time ? new Date(e.end_time).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : 'En cours'}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono font-semibold text-[var(--primary-400)]">
                  {e.hours_worked ? `${e.hours_worked}h` : '—'}
                </span>
                {e.status === 'validated' && (
                  <Badge variant="outline" className="bg-[var(--success)]/10 text-[var(--success)] border-[var(--success)] text-xs">
                    Validé
                  </Badge>
                )}
              </div>
            </div>
          ))}

          {displayEntries.length === 0 && (
            <div className="text-center py-8 text-[var(--text-secondary)]">
              <p className="text-sm">Aucune entrée ce mois-ci</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
